import { z } from 'zod'

import {
  workspaceSchema,
  createWorkspaceRequestSchema,
  createWorkspaceResponseSchema,
  workspaceUserSchema,
  inviteUserRequestSchema,
  inviteUserResponseSchema,
  acceptInviteRequestSchema,
  inviteInfoResponseSchema,
  rejectInviteRequestSchema,
  type CreateWorkspaceRequest,
  type CreateWorkspaceResponse,
  type WorkspaceUser,
  type InviteUserRequest,
  type InviteUserResponse,
  type AcceptInviteRequest,
  type InviteInfoResponse,
  type RejectInviteRequest,
} from '@/types/api/workspace'

import { apiClient } from './client'

export async function createWorkspace(data: CreateWorkspaceRequest): Promise<CreateWorkspaceResponse> {
  const validated = createWorkspaceRequestSchema.parse(data)
  const response = await apiClient.post('/workspaces/create', validated, {
    skipAuthRefresh: !!validated.loginToken,
  })
  return createWorkspaceResponseSchema.parse(response.data)
}

export async function getWorkspaces(): Promise<z.infer<typeof workspaceSchema>[]> {
  const response = await apiClient.get('/workspaces')
  return z.array(workspaceSchema).parse(response.data)
}

export async function getWorkspaceUsers(): Promise<WorkspaceUser[]> {
  const response = await apiClient.get('/workspace-users')
  return workspaceUserSchema.array().parse(response.data)
}

export async function inviteUser(data: InviteUserRequest): Promise<InviteUserResponse> {
  const validated = inviteUserRequestSchema.parse(data)
  const response = await apiClient.post('/workspace-invites/invite', validated)
  return inviteUserResponseSchema.parse(response.data)
}

export async function acceptInvite(data: AcceptInviteRequest): Promise<void> {
  const validated = acceptInviteRequestSchema.parse(data)
  await apiClient.post('/workspace-invites/accept-invite', validated)
}

export async function assignRole(userId: string, roleId: string): Promise<void> {
  await apiClient.post(`/workspace-users/${userId}/roles/${roleId}`)
}

export async function removeRole(userId: string, roleId: string): Promise<void> {
  await apiClient.delete(`/workspace-users/${userId}/roles/${roleId}`)
}

// Task 13 — invite info (public, no auth) + reject invite (authenticated)
export async function getInviteInfo(token: string): Promise<InviteInfoResponse> {
  const response = await apiClient.get('/workspace-invites/info', { params: { token } })
  return inviteInfoResponseSchema.parse(response.data)
}

export async function rejectInvite(data: RejectInviteRequest): Promise<void> {
  const validated = rejectInviteRequestSchema.parse(data)
  await apiClient.post('/workspace-invites/reject-invite', validated)
}
